import {
    clearAssignment,
    currentState,
    hasNewAssignment,
    hasUnseenAssignment,
    markAssignmentSeen,
    unseenAssignmentCount
} from './state';

const assignmentRowSelector = '[data-assignment-resource-id]';
const assignmentFunctionalElementSelector = [
    '.assignment-resource-view-details-trigger',
    '.assignment_resource__main_panel__form__resources__card__actions-settings__kebab_menu'
].join(', ');
const folderSelector = '.assignment-group-wrapper';
const folderToggleSelector = '.assignment_resource__main_panel__form__resources__card__primary-information-description-name.assignment-group';
const folderCardSelector = '.assignment-group-wrapper-information';
const folderContentSelector = '.collapse';
const dotSelector = '[data-assignment-resource-notification-dot="true"]';
const dotClass = 'assignment-resource-notification-dot';
const dotLabel = 'New or updated assignment';
const bottomNotificationId = 'assignment-resource-unseen-notification';
const bottomNotificationClass = 'assignment-resource-unseen-notification';
const bottomNotificationCaretClass = 'assignment-resource-unseen-notification__caret';
const bottomNotificationTextClass = 'assignment-resource-unseen-notification__text';
const scrollObserverRefreshDelay = 250;
const folderExpansionTimeout = 1000;

// Assignment row notification dots

const existingDot = (element) => element.querySelector(dotSelector);

const createDot = () => {
    const dot = document.createElement('span');

    dot.className = dotClass;
    dot.dataset.assignmentResourceNotificationDot = 'true';
    dot.setAttribute('aria-label', dotLabel);
    dot.setAttribute('role', 'status');

    return dot;
};

const showNotificationDot = (element) => {
    if (!element || existingDot(element)) return;

    element.appendChild(createDot());
};

const hideNotificationDot = (element) => {
    existingDot(element)?.remove();
};

const assignmentRowIsNewOrUpdated = (row) => hasNewAssignment(row.dataset.assignmentResourceId);

const applyAssignmentRowDots = () => {
    document.querySelectorAll(assignmentRowSelector).forEach((row) => {
        if (assignmentRowIsNewOrUpdated(row)) {
            showNotificationDot(row);
        } else {
            hideNotificationDot(row);
        }
    });
};

// Folder notification dots

const folderHasNotifiedAssignment = (folder) => {
    return Array.from(folder.querySelectorAll(assignmentRowSelector)).some(assignmentRowIsNewOrUpdated);
};

const folderIsOpen = (folder) => {
    const folderToggle = folder.querySelector(folderToggleSelector);
    const folderContent = folder.querySelector(folderContentSelector);

    return folderToggle?.getAttribute('aria-expanded') === 'true' || folderContent?.classList.contains('show');
};

const applyFolderDots = () => {
    document.querySelectorAll(folderSelector).forEach((folder) => {
        const folderCard = folder.querySelector(folderCardSelector);

        if (folderHasNotifiedAssignment(folder) && !folderIsOpen(folder)) {
            showNotificationDot(folderCard);
        } else {
            hideNotificationDot(folderCard);
        }
    });
};

// Bottom unseen assignment notification

const assignmentRows = () => Array.from(document.querySelectorAll(assignmentRowSelector));

const assignmentRowForId = (assignmentResourceId) => {
    const normalizedAssignmentResourceId = String(assignmentResourceId);
    return assignmentRows().find((row) => row.dataset.assignmentResourceId === normalizedAssignmentResourceId);
};

const isRowVisible = (row) => {
    const rect = row.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    return rect.bottom > 0 && rect.top < viewportHeight;
};

const unseenNotificationRows = () => {
    return assignmentRows().filter((row) => hasUnseenAssignment(row.dataset.assignmentResourceId));
};

const offscreenUnseenNotificationRows = () => {
    return unseenNotificationRows().filter((row) => !isRowVisible(row));
};

const unseenCountText = (count) => `${count} New ${count === 1 ? 'Assignment' : 'Assignments'}`;

const bottomNotification = () => document.getElementById(bottomNotificationId);

const createBottomNotification = () => {
    const button = document.createElement('button');

    button.id = bottomNotificationId;
    button.type = 'button';
    button.className = bottomNotificationClass;
    button.setAttribute('aria-live', 'polite');
    button.addEventListener('click', navigateToNextUnseenAssignment);
    document.body.appendChild(button);

    return button;
};

export const hideBottomNotification = () => {
    if (unseenObserver) {
        unseenObserver.disconnect();
        unseenObserver = null;
    }

    bottomNotification()?.remove();
};

const nextUnseenAssignmentRow = () => offscreenUnseenNotificationRows()[0] || unseenNotificationRows()[0];

const focusScrollTarget = (target) => {
    if (!target) return;

    if (!target.hasAttribute('tabindex')) {
        target.setAttribute('tabindex', '-1');
    }
    target.focus({ preventScroll: true });
};

const scrollToAssignment = (target) => {
    if (!target) return;

    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    focusScrollTarget(target);
    // Give smooth scrolling a moment to move the target before recalculating offscreen rows.
    window.setTimeout(updateBottomNotification, scrollObserverRefreshDelay);
};

const announceNavigationStatus = (message) => {
    window.announceAssignmentResourceStatus?.(message);
};

const notificationLocation = (assignmentResourceId) => {
    return new Promise((resolve, reject) => {
        $.ajax({
            url: `/assignment_resources/${encodeURIComponent(assignmentResourceId)}/notification_location`,
            type: 'GET',
            dataType: 'json'
        }).done(resolve).fail(reject);
    });
};

const loadTopLevelPagesUntil = async (targetExists) => {
    while (!targetExists() && window.hasMoreAssignmentResourcePages?.()) {
        if (typeof window.loadNextAssignmentResourcePage !== 'function') return false;

        const loaded = await window.loadNextAssignmentResourcePage();
        if (!loaded) return false;
    }

    return Boolean(targetExists());
};

const folderList = (resourceGroupId) => {
    return document.getElementById(`resource-group-${resourceGroupId}-assignments`);
};

const expandFolder = (resourceGroupId) => {
    const collapseElement = document.getElementById(`resourceGroupCollapse-${resourceGroupId}`);
    if (!collapseElement) return Promise.resolve(false);
    if (collapseElement.classList.contains('show')) return Promise.resolve(true);

    return new Promise((resolve) => {
        let expansionTimeout;
        const finish = () => {
            window.clearTimeout(expansionTimeout);
            $(collapseElement).off('shown.bs.collapse', finish);
            resolve(collapseElement.classList.contains('show'));
        };

        expansionTimeout = window.setTimeout(finish, folderExpansionTimeout);
        $(collapseElement).one('shown.bs.collapse', finish).collapse('show');
    });
};

const loadFolderPagesUntilAssignment = async (resourceGroupId, assignmentResourceId) => {
    while (!assignmentRowForId(assignmentResourceId) &&
           window.hasMoreAssignmentResourceFolderPages?.(resourceGroupId)) {
        if (typeof window.loadNextAssignmentResourceFolderPage !== 'function') return false;

        const loaded = await window.loadNextAssignmentResourceFolderPage(resourceGroupId);
        if (!loaded) return false;
    }

    return Boolean(assignmentRowForId(assignmentResourceId));
};

const loadNotificationAssignmentRow = async (assignmentResourceId) => {
    const location = await notificationLocation(assignmentResourceId);
    const resourceGroupId = location.resource_group_id;
    const topLevelTargetExists = resourceGroupId
        ? () => Boolean(folderList(resourceGroupId))
        : () => Boolean(assignmentRowForId(assignmentResourceId));

    if (!await loadTopLevelPagesUntil(topLevelTargetExists)) return null;

    if (resourceGroupId != null) {
        if (!await expandFolder(resourceGroupId)) return null;
        if (!await loadFolderPagesUntilAssignment(resourceGroupId, assignmentResourceId)) return null;
    }

    return assignmentRowForId(assignmentResourceId);
};

const revealLoadedAssignmentRow = async (row) => {
    const collapsedFolder = row.closest(`${folderSelector} ${folderContentSelector}:not(.show)`);
    if (!collapsedFolder) return row;

    const resourceGroupId = collapsedFolder.id.replace('resourceGroupCollapse-', '');
    return await expandFolder(resourceGroupId) ? row : row.closest(folderSelector);
};

const setNavigationLoadingState = (button, loading) => {
    if (!button?.isConnected) return;

    button.disabled = loading;
    if (loading) {
        button.setAttribute('aria-busy', 'true');
        button.innerHTML = `<span class="${bottomNotificationTextClass}">Loading updated assignment…</span>`;
    } else {
        button.removeAttribute('aria-busy');
    }
};

async function navigateToNextUnseenAssignment() {
    const button = bottomNotification();
    const loadedRow = nextUnseenAssignmentRow();
    const assignmentResourceId = loadedRow?.dataset.assignmentResourceId || currentState().unseenAssignmentIds[0];
    if (!assignmentResourceId) return;

    setNavigationLoadingState(button, true);
    announceNavigationStatus('Loading updated assignment.');

    try {
        const row = loadedRow
            ? await revealLoadedAssignmentRow(loadedRow)
            : await loadNotificationAssignmentRow(assignmentResourceId);

        if (!row) {
            announceNavigationStatus('The updated assignment could not be shown. Clear any filters and try again.');
            return;
        }

        scrollToAssignment(row);
        announceNavigationStatus('Updated assignment loaded.');
    } catch (_error) {
        announceNavigationStatus('The updated assignment could not be loaded. Please try again.');
    } finally {
        setNavigationLoadingState(button, false);
        updateBottomNotification();
    }
}

const renderBottomNotification = () => {
    const unseenCount = unseenAssignmentCount();

    if (unseenCount === 0) {
        hideBottomNotification();
        return;
    }

    const button = bottomNotification() || createBottomNotification();
    const text = unseenCountText(unseenCount);

    button.innerHTML = `<span class="${bottomNotificationCaretClass}" aria-hidden="true">⌄</span><span class="${bottomNotificationTextClass}">${text}</span>`;
    button.setAttribute('aria-label', text);
};

const markVisibleUnseenAssignments = () => {
    unseenNotificationRows().forEach((row) => {
        if (isRowVisible(row)) {
            markAssignmentSeen(row.dataset.assignmentResourceId);
        }
    });
};

let unseenObserver = null;

const observeUnseenAssignments = () => {
    if (unseenObserver) {
        unseenObserver.disconnect();
    }

    if (!('IntersectionObserver' in window)) return;

    // Keep the bottom notification count in sync as unseen rows enter the viewport naturally.
    unseenObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                markAssignmentSeen(entry.target.dataset.assignmentResourceId);
                unseenObserver.unobserve(entry.target);
            }
        });
        renderBottomNotification();
    }, { threshold: 0.25 });

    unseenNotificationRows().forEach((row) => {
        unseenObserver.observe(row);
    });
};

// Event binding

const clearAssignmentDotForRow = (row) => {
    clearAssignment(row.dataset.assignmentResourceId);
    applyAssignmentDots();
};

const assignmentRowForEvent = (event) => {
    const functionalElement = event.target.closest(assignmentFunctionalElementSelector);
    return functionalElement?.closest(assignmentRowSelector);
};

const bindAssignmentDotInteractionHandlers = () => {
    if (document.assignmentResourceNotificationDotHandlersBound) return;

    document.assignmentResourceNotificationDotHandlersBound = true;
    document.addEventListener('click', function (event) {
        const assignmentRow = assignmentRowForEvent(event);

        if (assignmentRow) {
            clearAssignmentDotForRow(assignmentRow);
            return;
        }

        if (event.target.closest(folderToggleSelector)) {
            window.setTimeout(applyFolderDots, 0);
        }
    });
};

export const applyAssignmentDots = () => {
    bindAssignmentDotInteractionHandlers();
    applyAssignmentRowDots();
    applyFolderDots();
};

export const updateBottomNotification = () => {
    if (unseenAssignmentCount() === 0) {
        hideBottomNotification();
        return;
    }

    markVisibleUnseenAssignments();
    observeUnseenAssignments();
    renderBottomNotification();
};
